import { updateResource, listResource, apiFetch, authenticatedFetch } from '@diggens/api-client';
import type { ListParams } from '@diggens/api-client';
import type { User, UserPermissions } from '../types';

export async function updateUserProfile(
  userId: string,
  data: {
    first_name?: string;
    last_name?: string;
    organization?: string;
  }
): Promise<User> {
  return updateResource<User>('users', userId, data);
}

export async function listUsers(tenantId?: string): Promise<User[]> {
  const params: ListParams = { order: 'created_at.desc' };
  if (tenantId) {
    params.tenant_id = tenantId;
  }
  const { data } = await listResource<User>('users', params);
  return data;
}

export async function createUser(userData: { email: string; first_name: string; last_name: string; role: string; tenant_id: string; organization?: string }): Promise<User> {
  return apiFetch<User>('/users/invite', {
    method: 'POST',
    body: JSON.stringify(userData),
  });
}

export async function updateUserRole(userId: string, role: string): Promise<User> {
  return updateResource<User>('users', userId, { role });
}

export async function updateUserPermissions(userId: string, permissions: UserPermissions): Promise<User> {
  return updateResource<User>('users', userId, { user_permissions: permissions });
}

export async function resetUserPassword(userId: string): Promise<void> {
  const response = await authenticatedFetch(`/users/${userId}/reset-password`, {
    method: 'POST',
    body: JSON.stringify({ redirect_to: `${window.location.origin}/auth/set-password` }),
  });
  if (!response.ok) throw new Error('Failed to reset password');
}

export async function deactivateUser(userId: string): Promise<void> {
  const response = await authenticatedFetch(`/users/${userId}/deactivate`, {
    method: 'PATCH',
  });
  if (!response.ok) throw new Error('Failed to deactivate user');
}

export async function activateUser(userId: string): Promise<void> {
  const response = await authenticatedFetch(`/users/${userId}/activate`, {
    method: 'PATCH',
  });
  if (!response.ok) throw new Error('Failed to activate user');
}

export async function changeUserRole(userId: string, role: string): Promise<void> {
  const response = await authenticatedFetch(`/users/${userId}/role`, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  });
  if (!response.ok) throw new Error('Failed to change role');
}

export async function deleteUser(userId: string): Promise<void> {
  const response = await authenticatedFetch(`/users/${userId}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error('Failed to delete user');
}

export async function forceLogout(userId: string): Promise<void> {
  const response = await authenticatedFetch(`/users/${userId}/force-logout`, {
    method: 'POST',
  });
  if (!response.ok) throw new Error('Failed to force logout');
}
